/***
*
*   VIEW
*   The view houses global components that are common to all views
*   (notification, modal), handles errors and renders the layout
*
**********/

import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout, AuthLayout, AccountListLayout, FirmLayout, OnboardingLayout, Modal, Notification, Loader, History, SyncToast } from '../lib';
import { useSocket } from '../../app/socketContext';
import { ALL_SYNC_STAGES } from '../../constants/syncStages';

import './scss/normalize.scss';
import './scss/view.scss';
import './scss/typography.scss';

export const ViewContext = React.createContext();

export function View(props){

  // state
  const [notification, setNotification] = useState({ visible: 'hide', autoclose: true });
  const [modal, setModal] = useState({});
  const [loading, setLoading] = useState(false);
  const [persistentNotification] = useState(null);

  // Use socket context for messages
  const { message } = useSocket();

  const hideNotification = useCallback(() => {
    setNotification({
      text: '',
      type: '',
      show: false,
      format: null,
      icon: null,
      autoclose: true,
      url: undefined,
      urlText: undefined
    });
  }, []);

  const showNotification = useCallback((text, type, autoclose, format, icon, url=undefined, urlText=undefined, inline=false, noclose=false) => {
    setNotification({
      text: text,
      type: type,
      show: true,
      format: format,
      icon: icon,
      autoclose: autoclose,
      url: url,
      urlText: urlText,
      inline: inline,
      noclose: noclose
    });

    if (autoclose) setTimeout(hideNotification, 4000);
  }, [hideNotification]);

  useEffect(() => {
    if (message && message.message) {
      // Parse socket message to check if it's sync-related
      let parsedMessage;
      try {
        parsedMessage = JSON.parse(message.message);
      } catch {
        parsedMessage = { stage: null, message: message.message };
      }
      
      // Don't show ViewContext notifications for sync messages - they're handled by SyncToast
      if (parsedMessage.stage && ALL_SYNC_STAGES.includes(parsedMessage.stage)) {
        return; // Skip ViewContext notifications for sync messages
      }
      
      if (!parsedMessage.stage) {
        // Fallback for unstructured messages (non-sync related)
        showNotification(message.message, 'success', true, 'toast', 'heart');
      }
    }
  }, [message, showNotification]);

  // layouts
  const layouts = {

    app: AppLayout,
    auth: AuthLayout,
    onboarding: OnboardingLayout,
    accountList: AccountListLayout,
    firm: FirmLayout 
  }

  // set title & layout
  document.title = props.title;
  let Layout = props.layout ? layouts[props.layout] : AppLayout;

  if (!props.display)
    return false;



  function showModal(content, callback){

    let data = {...modal }

    if (content){

      for (let key in content)
        data[key] = content[key];

      data.show = true;
      data.callback = callback;

    }

    setModal(data);

  }

  function hideModal(cancel, res){

    // callback?
    if (!cancel && modal.callback)
      modal.callback(modal.form, res);

    // reset the modal
    setModal({

      title: null,
      text: null,
      buttonText: null,
      url: null,
      method: null,
      show: false,

    });
  }

  function handleError(err){

    let message = 'There was a glitch in the matrix – please try again';

    if (err){

      message = err.toString();

      if (err.response){

        if (err.response.data?.message)
          message = err.response.data.message

        if (err.response.status){
          switch (err.response.status){

            case 401:
            History.push('/signin');
            break;

            case 404:
            History.push('/notfound');
            break;

            case 429:
            showNotification(err.response.data, 'error', false);
            break;

            case 402: // payment required
            History.push('/account/upgrade?plan=' + err.response.data.plan);
            break;

            default:
            console.error(err);
            showNotification(message, 'error', false);
            break;

          }
        }
      }
    }
  }

  const context = {

    notification: {

      show: showNotification,
      hide: hideNotification,
      data: notification,

    },
    modal: {

      show: showModal,
      hide: hideModal,
      data: modal,

    },

    setLoading: state => setLoading(state),
    handleError: handleError

  }

  return (
    <ViewContext.Provider value={ context }>

      { notification.show &&
        <Notification {...notification }/> }
        
      { persistentNotification?.show &&
        <Notification {...persistentNotification }/> }

      { loading && 
        <Loader fullScreen /> }

      { modal.show &&
        <Modal {...modal } /> }

      <SyncToast />

        <Layout title={ props.title } data={ props.data }>
          { props.display }
        </Layout>

    </ViewContext.Provider>
  );
}
